import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

/** Every field keeps the same rhythm: 1 column on phones, 2 on tablets, 3 on desktop. */
export const FIELD_SIZE = { xs: 12, sm: 6, md: 4 } as const;

/**
 * Titled card used to group the fields of a trainee profile tab.
 * Children are placed inside a Grid container, so pass Grid items
 * (TextInput/SelectInput already render one).
 */
export function Section({
  title,
  description,
  action,
  children
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        borderColor: 'divider'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Grid container columnSpacing={2}>
        {children}
      </Grid>
    </Paper>
  );
}

/** Action bar pinned to the bottom of a tab: stacked on phones, right aligned above. */
export function StickyActions({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={1.5}
        justifyContent="flex-end"
      >
        {children}
      </Stack>
    </Box>
  );
}

export default Section;
